/* Floating add-to-cart bar: selection + price before/after the discount.
 *
 * <sticky-bar-details> sits inside <product-sticky-bar> (sections/main-product.liquid,
 * "sticky_bar_details" setting) and mirrors what the shopper has chosen on the page:
 *
 *   24"x16" · Walnut Frame
 *   $140.00                               until the sitewide code is applied
 *   $98.00  $140.00  AUTUMN30 applied     once it is (same moment the PDP price switches)
 *
 * It never calculates a price of its own. The amount comes from the main price
 * element's data-price-cents (kept current by the master picker, or by the theme's
 * re-render on native products) and the discount from GooliArtGlobalDiscount.quote()
 * in custom.js - the same code, applied state and maths as the PDP price and the
 * discount box, so the bar cannot disagree with them.
 *
 * The bar is inside a <product-rerender>, so on native products the element is
 * replaced on every variant change: everything is (re)built in connectedCallback.
 */
(() => {
  if (window.customElements.get('sticky-bar-details')) return;

  const TRAILING_NOTE = /\s*\([^)]*\)\s*$/;   // "Walnut Frame (Ready to hang)" -> "Walnut Frame"

  const text = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');
  const toCents = (money) => {
    const n = parseFloat(String(money).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : NaN;
  };

  class StickyBarDetails extends HTMLElement {
    connectedCallback() {
      this.selectionEl = this.querySelector('[data-sbd-selection]');
      this.typeEl = this.querySelector('[data-sbd-type]');
      this.nowEl = this.querySelector('[data-sbd-now]');
      this.wasEl = this.querySelector('[data-sbd-was]');
      this.tagEl = this.querySelector('[data-sbd-tag]');

      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      const schedule = () => this.schedule();

      document.addEventListener('variant:change', schedule, options);
      document.addEventListener('gooliart:discount-refresh', schedule, options);
      document.addEventListener('no-default:picks', schedule, options);
      // in / cm switch relabels the sizes
      document.addEventListener('change', (event) => {
        if (/size-unit/.test((event.target && event.target.name) || '')) this.schedule();
      }, options);

      const bar = this.closest('product-sticky-bar');
      if (bar) {
        // Re-read at the moment the bar slides in. The server-rendered price is the theme
        // product's first variant ($75 on a master design whose default selection is $140);
        // the master picker loads its real variants later and does not always announce it.
        this.visibilityObserver = new MutationObserver(() => {
          const showing = bar.classList.contains('is-visible');
          if (showing) this.update();
          // Lets CSS move other corner widgets out of the bar's way on phones - the Attentive
          // sign-up bubble would otherwise sit on top of the selection and price (custom.css).
          const root = document.documentElement;
          root.classList.toggle('sticky-bar-showing', showing);
          root.style.setProperty('--sticky-bar-height', `${showing ? bar.offsetHeight : 0}px`);
        });
        this.visibilityObserver.observe(bar, { attributes: true, attributeFilter: ['class'] });

        bar.addEventListener('click', (event) => {
          if (!event.target.closest('button[type="submit"]')) return;
          try { (window.dataLayer = window.dataLayer || []).push({ event: 'sticky_bar_add_to_cart' }); } catch (e) {}
        }, options);
      }

      this.schedule();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.visibilityObserver?.disconnect();
      document.documentElement.classList.remove('sticky-bar-showing');
      clearTimeout(this.timer);
    }

    // The master picker announces variant:change BEFORE it rewrites the price block,
    // and it loads its variants after first paint: read now, and once more shortly after.
    schedule() {
      this.update();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(), 400);
    }

    scope() {
      return this.closest('.shopify-section') || document;
    }

    update() {
      this.updateSelection();
      this.updatePrices();
    }

    // --- what is selected --------------------------------------------------
    updateSelection() {
      const scope = this.scope();
      // rows the no-default A/B (no-default-pdp.js) still shows as unpicked are not part of the selection yet
      const checked = Array.from(scope.querySelectorAll('input[type="radio"]:checked[data-option-name]'))
        .filter((input) => !input.closest('[data-nd-unpicked]'));
      const find = (name) => checked.find((input) => input.dataset.optionName === name);
      const parts = [];

      const size = find('Size');
      if (size) parts.push(this.sizeLabel(size));
      const frame = find('Frame');
      if (frame) parts.push((frame.dataset.optionValue || '').replace(TRAILING_NOTE, ''));

      // products with other option names (sets, gift cards...): list what is chosen (the type has its own label;
      // a size or frame the no-default A/B still has unpicked is not "other")
      if (!parts.length && !scope.querySelector('input[data-option-name="Size"], input[data-option-name="Frame"]')) {
        checked.filter((input) => input.dataset.optionName !== 'Type')
          .forEach((input) => parts.push((input.dataset.optionValue || '').replace(TRAILING_NOTE, '')));
      }

      if (this.selectionEl) this.selectionEl.textContent = parts.filter(Boolean).join(' · ');

      if (this.typeEl) {
        const picker = scope.querySelector('master-variant-picker');
        const options = picker && picker.masterVariantsData && picker.masterVariantsData.options;
        const typeIndex = options ? options.findIndex((option) => option.name === 'Type') : -1;
        const type = typeIndex >= 0 ? picker.selectedOptions[`option${typeIndex + 1}`] : (find('Type') || {}).dataset?.optionValue;
        this.typeEl.textContent = type || '';
        this.typeEl.hidden = !type;
      }
    }

    // the label the shopper sees (follows the in / cm switch); a split size
    // "72x48 | 36x48 ea." reads "72"x48" · 2 panels"
    sizeLabel(input) {
      const value = input.dataset.optionValue || '';
      const label = input.nextElementSibling || (input.id && this.scope().querySelector(`label[for="${CSS.escape(input.id)}"]`));
      // labels also carry badges ("2-piece", "Offer"): keep only the first WxH in them
      const dimension = text(label).match(/\d+(?:\.\d+)?\s*(?:["″]|cm)?\s*[x×]\s*\d+(?:\.\d+)?\s*(?:["″]|cm)?/i);
      const shown = dimension ? dimension[0].replace(/\s+/g, '') : value.split('|')[0].trim();
      // plain "24x16", or a set's "3 pcs 18x24 each": the value, with its WxH as the shopper sees it
      if (!value.includes('|')) return value.replace(/\d+(?:\.\d+)?\s*x\s*\d+(?:\.\d+)?/i, shown);

      const full = parseFloat(value), each = parseFloat(value.split('|')[1]);
      const panels = each > 0 ? Math.round(full / each) : 0;
      return panels > 1 ? `${shown} · ${panels} panels` : shown;
    }

    // --- prices --------------------------------------------------------------
    updatePrices() {
      if (!this.nowEl) return;
      const priceEl = this.scope().querySelector('[data-block-type="price"] [data-discount-price]');
      if (!priceEl) return;

      const cents = Number(priceEl.dataset.priceCents);
      const quote = window.GooliArtGlobalDiscount && window.GooliArtGlobalDiscount.quote
        ? window.GooliArtGlobalDiscount.quote(cents)
        : null;

      // Only once the code is really applied (Apply button, discount link, the shopper's own
      // code) - exactly when the PDP price itself switches. An un-applied code is an offer the
      // discount box makes; the bar must not show a price the cart would not charge.
      if (quote && quote.applied) {
        this.paint(quote.discounted, quote.original, `${quote.code} applied`, true);
        return;
      }

      // otherwise the price as it stands, with the product's own compare-at price if it has one
      const priceList = priceEl.closest('price-list');
      const compareEl = priceList && priceList.querySelector('compare-at-price:not([data-global-discount-compare])');
      const compareShown = compareEl && !compareEl.hidden && compareEl.style.display !== 'none';
      const compare = compareShown ? text(compareEl).replace(/^[^\d$€£]*/, '') : '';
      const compareCents = toCents(compare);
      const saving = compareCents > cents && cents > 0 ? Math.round((1 - cents / compareCents) * 100) : 0;

      this.paint(text(priceEl), saving ? compare : '', saving ? `Save ${saving}%` : '', false);
    }

    paint(now, was, tag, applied) {
      this.nowEl.textContent = now;
      this.nowEl.classList.toggle('is-discounted', !!was);
      if (this.wasEl) { this.wasEl.textContent = was; this.wasEl.hidden = !was; }
      if (this.tagEl) {
        this.tagEl.textContent = tag;
        this.tagEl.hidden = !tag;
        this.tagEl.classList.toggle('is-applied', !!applied);
      }
    }
  }

  window.customElements.define('sticky-bar-details', StickyBarDetails);
})();
